import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Couleurs par rôle
const roleColors = {
  admin: 'bg-violet-500',
  technicien: 'bg-blue-500',
  housekeeper: 'bg-emerald-500',
  gouvernant: 'bg-amber-500',
  default: 'bg-slate-400'
};

/**
 * Composant Avatar utilisateur avec fallback automatique sur initiales
 * Supporte les URLs Cloudinary et les chemins locaux
 */
const UserAvatar = ({ 
  user, 
  size = 'md', 
  className = '', 
  showBorder = false,
  borderColor = 'border-white'
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const sizeClasses = {
    xs: 'w-6 h-6 text-[10px]',
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-xl'
  };
  
  const sizeClass = sizeClasses[size] || sizeClasses.md;
  const bgColor = roleColors[user?.role] || roleColors.default;
  const borderClass = showBorder ? `border-2 ${borderColor}` : '';
  
  // Reset l'état quand l'utilisateur change
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [user?.avatar_url, user?.id]);
  
  // Obtenir les initiales
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  
  const initials = getInitials(user?.name);
  
  // Construire l'URL de l'avatar
  // Si c'est une URL complète (Cloudinary), l'utiliser directement
  // Sinon, préfixer avec l'API URL (ancien système local)
  const getAvatarUrl = () => {
    const avatarUrl = user?.avatar_url;
    if (!avatarUrl) return null;
    
    // URL Cloudinary complète (commence par http)
    if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
      return avatarUrl;
    }
    
    // Ancien système local
    return `${API_URL}/api/${avatarUrl}`;
  };
  
  const avatarUrl = getAvatarUrl();
  const hasValidAvatar = avatarUrl && !imageError;
  
  return (
    <div className={`relative inline-flex flex-shrink-0 ${className}`}>
      {/* Fallback initiales - toujours visible en arrière-plan */}
      <div 
        className={`${sizeClass} ${bgColor} ${borderClass} rounded-full flex items-center justify-center text-white font-bold shadow-md ring-2 ring-white`}
      >
        {initials}
      </div>
      
      {/* Image superposée - visible seulement si chargée */}
      {hasValidAvatar && (
        <img
          src={avatarUrl}
          alt={user?.name || 'Avatar'}
          className={`absolute inset-0 ${sizeClass} rounded-full object-cover ${borderClass} shadow-md ring-2 ring-white transition-opacity duration-200 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          loading="lazy"
        />
      )}
    </div>
  );
};

export default UserAvatar;
